import React, { memo, useEffect, useState, useCallback, useMemo } from "react";
import store from "../../../store";
import { useSnapshot } from "valtio";
import loadCategories from "./../../../services/loadCategories";
import addNewGraphicElement from "../../../util/internal/elements/addNewGraphicElement";
import addNewUploadedElement from "../../../util/internal/elements/addNewUploadedElement";
import getAfterTripleAt from "../../../util/order/getStringAfterTripleAt";

import ThemeElement from "../../../util/theme/ThemeElement";

// selectedSectionId is stored as "i-j@@@<uuid>"; categories reference the product-profile section uuid
const toProfileSectionId = (sectionId) => {
    if (sectionId === undefined || sectionId === null) return null;
    const id = String(sectionId);
    return id.includes('@@@') ? getAfterTripleAt(id) : id;
};

// Generic categories belong to every section, the rest only to the sections they are assigned to
const isCategoryAvailableForSection = (category, sectionId) => {
    if (category.generic) return true;
    return sectionId !== null && (category.sectionIds || []).includes(sectionId);
};

const GraphicTab = memo(() => {
    const snap = useSnapshot(store);
    const selectedSectionId = snap?.selectedSectionId;
    const graphicCategories = snap?.ui.graphicCategories;
    const [isLoading, setIsLoading] = useState(false);
    const [mediaList, setMedia] = useState();
    const [filteredList, setFilteredList] = useState([]);
    const [selectedCategoryList, setSelectedCategoryList] = useState([]);
    const [inProgress, setInProgress] = useState(false);

    // Categories (and their images) available for the selected section
    const visibleList = useMemo(() => {
        if (!mediaList) return mediaList;
        const sectionId = toProfileSectionId(selectedSectionId);
        const visibleCategoryIds = new Set(
            mediaList
                .filter(item => item.type === 'category' && isCategoryAvailableForSection(item, sectionId))
                .map(item => item.id)
        );
        return mediaList.filter(item => visibleCategoryIds.has(item.type === 'category' ? item.id : item.parent));
    }, [mediaList, selectedSectionId]);


    // handle click on category or graphic item
    const onClick = useCallback((item) => {
        if (item.type === 'category') {
            setSelectedCategoryList((prevList) => {
                const newList = [...prevList, item];
                handleFilterList(item.id);
                return newList;
            });
        } else {
            addNewGraphicElement(item.img, selectedSectionId);
        }
    }, [selectedSectionId, visibleList]);
    
    // Handle get file
    const handleUpload = useCallback(async (e) => {
        const isValidFileType = (file) => {
            const allowedTypes = ['image/png', 'image/jpeg', 'image/bmp', 'image/svg+xml'];
            return file && allowedTypes.includes(file.type);
        };
        
        setInProgress(true);
        const file = e.target.files[0];
        if (isValidFileType(file)) {
            try {
                store.editElement = await addNewUploadedElement(file, selectedSectionId);
            } catch (error) {
                console.error("Error uploading image:", error);
            } finally {
                setInProgress(false);
            }
        }
        
        e.target.value = null;
    }, [selectedSectionId]);
    
    // Handle Load categories
    const handleLoadCategories = useCallback(async () => {
        setIsLoading(true);
        let list = [];
        if (graphicCategories) {
            list = JSON.parse(localStorage.getItem("graphicCategories"));
        } else {
            list = await loadCategories();
            list = list.filter(category => category.is_active && category.images.length > 0);
            list.sort((a, b) =>
                (b.show_on_home - a.show_on_home) ||
                (a.priority - b.priority) ||
                a.name.localeCompare(b.name)
            );
            
            const flattedList = list.reduce((acc, category) => {
                acc.push({
                    id: category.id,
                    img: category.category_image_url,
                    description: category.description,
                    name: category.name,
                    type: 'category',
                    parent: category.show_on_home ? null : category.parent_id,
                    showOnHome: category.show_on_home,
                    // API resolves a stored NULL to true, so an absent flag means generic
                    generic: category.generic !== false,
                    sectionIds: (category.sections || []).map(section => String(section.id)),
                });
                acc.push(...category.images.sort((a, b) => String(a.id).localeCompare(String(b.id))).map((image) => ({
                    id: image.id,
                    img: image.url,
                    description: category.description,
                    name: category.name,
                    type: 'image',
                    parent: category.id,
                    showOnHome: false,
                })));
                return acc;
            }, []);
            store.ui.graphicCategories = true;
            localStorage.setItem("graphicCategories", JSON.stringify(flattedList));
            list = flattedList;
        }
        setMedia(list);
        setIsLoading(false);
    }, [graphicCategories]);

    // Handle filter list
    const handleFilterList = (parentId = null, searchString = null) => {
        if (visibleList) {
            let list = visibleList.filter(item =>
                item.parent === parentId ||
                (parentId === null ? item.showOnHome : false)
            );
            
            if (searchString) {
                list = list.filter(item =>
                    item.name.toLowerCase().includes(searchString.toLowerCase()) ||
                    item.description.toLowerCase().includes(searchString.toLowerCase())
                );
            }
            setFilteredList(list);
        }
    };
    
    // Handle search on list
    const handleSearch = useCallback((text) => {
        const parentId = selectedCategoryList.length > 0 ? selectedCategoryList[selectedCategoryList.length - 1].id : null;
        handleFilterList(parentId, text);
    }, [selectedCategoryList, visibleList]);
    
    // Handle Remove Tag
    const handleRemoveTag = useCallback((id) => {
        setSelectedCategoryList((prevList) => {
            const index = prevList.findIndex(item => item.id === id);
            if (index !== -1) {
                const newList = prevList.slice(0, index);
                const parentId = newList.length > 0 ? newList[newList.length - 1].id : null;
                handleFilterList(parentId);
                return newList;
            }
            return prevList;
        });
    }, [handleFilterList, visibleList]);
    
    
    // Handle Ai Image Generator
    const handleAiImageGenerator = useCallback(async()=> {
        //const blob =  await createImageBlob();
        //const b = await addNewAiImageElement(blob, selectedSectionId);
        store.editElement = {
            type: "aiImage"
        }
        store.ui.addModalVisibility = false
    },[selectedSectionId]);
    
    // Initial and loading categories data and filter list
    useEffect(() => {
        handleLoadCategories();
    }, []);
    
    
    // Refilter when categories load or the section changes; a category reserved for
    // another section must not stay open across a switch
    useEffect(() => {
        if (visibleList) {
            const visibleIds = new Set(visibleList.map(item => item.id));
            setSelectedCategoryList((prevList) => {
                const hiddenIndex = prevList.findIndex(item => !visibleIds.has(item.id));
                const newList = hiddenIndex === -1 ? prevList : prevList.slice(0, hiddenIndex);
                handleFilterList(newList.length > 0 ? newList[newList.length - 1].id : null);
                return newList;
            });
        }
    }, [visibleList]);
    
    
    
    return (
        <ThemeElement
            name={"AddElementGraphic"}
            data={{
                inProgress,
                handleUpload,
                isLoading,
                content: filteredList,
                onClick,
                handleSearch,
                handleRemoveTag,
                tags: selectedCategoryList.map(item => ({
                    icon: item.render,
                    title: item.name,
                    id: item.id
                })),
                aiImageGenerator: handleAiImageGenerator
            }}
        />
    );
});

export default GraphicTab;
